import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trackPageview, trackDwell, initClickTracking } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/marketing/Landing";
import { ForeignWorkforcePage, SubunternehmerPage, ZeiterfassungPage, HardwarePage } from "@/pages/marketing/ProductPages";
import { ZeiterfassungPflichtPage, NfcZeiterfassungPage, ComparisonPage, RatgeberHub, GuidePage, BlogPostPage } from "@/pages/marketing/SeoPages";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import SubContracts from "@/pages/employer/subcontractor/Contracts";
import SubContractWizard from "@/pages/employer/subcontractor/ContractWizard";
import AdminSubconRules from "@/pages/admin/SubconRules";
import { CareGate } from "@/components/CareGate";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import Activate from "@/pages/invite/Activate";
import Privacy from "@/pages/legal/Privacy";
import Imprint from "@/pages/legal/Imprint";
import Terms from "@/pages/legal/Terms";

import EmployerLayout from "@/components/layouts/EmployerLayout";
import EmployerOverview from "@/pages/employer/Overview";
import Employees from "@/pages/employer/Employees";
import EmployeeForm from "@/pages/employer/EmployeeForm";
import EmployeeWizard from "@/pages/employer/EmployeeWizard";
import ImmigrationCases from "@/pages/employer/ImmigrationCases";
import ImmigrationCaseDetail from "@/pages/employer/ImmigrationCaseDetail";
import Contracts from "@/pages/employer/Contracts";
import ContractDetail from "@/pages/employer/ContractDetail";
import ServiceOrders from "@/pages/admin/ServiceOrders";
import ImmigrationRules from "@/pages/admin/ImmigrationRules";
import EmployeeDetail from "@/pages/employer/EmployeeDetail";
import EmployerDocuments from "@/pages/employer/Documents";
import EmployerDeadlines from "@/pages/employer/Deadlines";
import EmployerActivity from "@/pages/employer/Activity";
import Company from "@/pages/employer/Company";
import Renewals from "@/pages/employer/Renewals";
import EmployerComplianceNews from "@/pages/employer/ComplianceNews";
import Billing from "@/pages/employer/Billing";
import Subcontractors from "@/pages/employer/Subcontractors";
import Baustellen from "@/pages/employer/Baustellen";
import BaustelleDetail from "@/pages/employer/BaustelleDetail";
import SubOverview from "@/pages/employer/subcontractor/Overview";
import SubCompanies from "@/pages/employer/subcontractor/Companies";
import SubCompanyDetail from "@/pages/employer/subcontractor/CompanyDetail";
import SubWorkers from "@/pages/employer/subcontractor/Workers";
import SubDocuments from "@/pages/employer/subcontractor/Documents";
import SubCompliance from "@/pages/employer/subcontractor/Compliance";
import SubAlerts from "@/pages/employer/subcontractor/Alerts";
import SubReports from "@/pages/employer/subcontractor/Reports";
import AdminTaxOffices from "@/pages/admin/TaxOffices";
import AdminReferenceData from "@/pages/admin/ReferenceData";
import AdminHardware from "@/pages/admin/Hardware";
import AdminOrders from "@/pages/admin/Orders";
import AdminNfcTags from "@/pages/admin/NfcTags";
import AdminComplianceNews from "@/pages/admin/ComplianceNews";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import EmployerSettings from "@/pages/employer/Settings";

import WorkerLayout from "@/components/layouts/WorkerLayout";
import WorkerOverview from "@/pages/worker/Overview";
import WorkerDocuments from "@/pages/worker/Documents";
import WorkerDeadlines from "@/pages/worker/Deadlines";
import WorkerProfile from "@/pages/worker/Profile";
import WorkerSettings from "@/pages/worker/Settings";
import WorkerMyTime from "@/pages/worker/MyTime";
import ClockScreen from "@/pages/public/ClockScreen";
import ClockTerminal from "@/pages/public/ClockTerminal";
import PinReveal from "@/pages/public/PinReveal";
import TimeDashboard from "@/pages/employer/TimeDashboard";
import TimeUpgrade from "@/pages/employer/TimeUpgrade";
import TimeFinancials from "@/pages/employer/TimeFinancials";
import ComplianceCenter from "@/pages/employer/ComplianceCenter";
import CorrectionRequests from "@/pages/employer/CorrectionRequests";
import ProfileChangeRequests from "@/pages/employer/ProfileChangeRequests";
import DocumentVerifications from "@/pages/employer/DocumentVerifications";
import EmployerModules from "@/pages/employer/Modules";
import CarePflege from "@/pages/employer/CarePflege";
import CarePatients from "@/pages/employer/CarePatients";
import CarePatientNew from "@/pages/employer/CarePatientNew";
import CarePatientDetail from "@/pages/employer/CarePatientDetail";
import CareDispatch from "@/pages/employer/CareDispatch";
import CareTours from "@/pages/employer/CareTours";
import CareTourDetail from "@/pages/employer/CareTourDetail";
import CareVisits from "@/pages/employer/CareVisits";
import CareVisitNew from "@/pages/employer/CareVisitNew";
import CareVisitDetail from "@/pages/employer/CareVisitDetail";
import WorkerCareTour from "@/pages/worker/CareTour";
import WorkerCareVisitDetail from "@/pages/worker/CareVisitDetail";
import WorkerCareDay from "@/pages/worker/CareDay";
import CareTags from "@/pages/employer/CareTags";
import CareReports from "@/pages/employer/CareReports";
import CareScan from "@/pages/public/CareScan";
import AdminCareTags from "@/pages/admin/AdminCareTags";
import TimeReports from "@/pages/employer/TimeReports";
import TimeHardware from "@/pages/employer/TimeHardware";
import TimeTagAnalytics from "@/pages/employer/TimeTagAnalytics";
import NfcDashboard from "@/pages/employer/NfcDashboard";
import SiteAccess from "@/pages/employer/SiteAccess";

import AdminLayout from "@/components/layouts/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminCompanies from "@/pages/admin/Companies";
import AdminCompanyDetail from "@/pages/admin/CompanyDetail";
import AdminUsers from "@/pages/admin/Users";
import AdminEmployees from "@/pages/admin/Employees";
import AdminActivity from "@/pages/admin/Activity";
import AdminAudit from "@/pages/admin/AuditLogs";
import AdminReminders from "@/pages/admin/Reminders";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import AdminTraffic from "@/pages/admin/Traffic";
import AdminSettings from "@/pages/admin/Settings";
import AdminSupport from "@/pages/admin/Support";
import AdminFullGuard from "@/components/AdminFullGuard";
import SupportPage from "@/pages/shared/SupportPage";
import Team from "@/pages/employer/Team";

const EMP = ["COMPANY_OWNER", "COMPANY_ADMIN", "MANAGER"];
const ADM = ["SUPER_ADMIN", "ADMIN", "MODERATOR"];

function Analytics() {
  const loc = useLocation();
  useEffect(() => { initClickTracking(); }, []);
  useEffect(() => {
    trackPageview();
    const onHide = () => { if (document.visibilityState === "hidden") trackDwell(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", trackDwell);
    return () => { trackDwell(); document.removeEventListener("visibilitychange", onHide); window.removeEventListener("beforeunload", trackDwell); };
  }, [loc.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Analytics />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/foreign-workforce" element={<ForeignWorkforcePage />} />
            <Route path="/subunternehmer" element={<SubunternehmerPage />} />
            <Route path="/zeiterfassung" element={<ZeiterfassungPage />} />
            <Route path="/hardware" element={<HardwarePage />} />
            <Route path="/zeiterfassung-pflicht-2026" element={<ZeiterfassungPflichtPage />} />
            <Route path="/nfc-zeiterfassung" element={<NfcZeiterfassungPage />} />
            <Route path="/clockin-alternative" element={<ComparisonPage k="clockin" />} />
            <Route path="/papershift-alternative" element={<ComparisonPage k="papershift" />} />
            <Route path="/timr-alternative" element={<ComparisonPage k="timr" />} />
            <Route path="/zep-alternative" element={<ComparisonPage k="zep" />} />
            <Route path="/ratgeber" element={<RatgeberHub />} />
            <Route path="/ratgeber/milog" element={<GuidePage slug="milog" />} />
            <Route path="/ratgeber/ruhezeiten" element={<GuidePage slug="ruhezeiten" />} />
            <Route path="/ratgeber/scheinselbststaendigkeit" element={<GuidePage slug="scheinselbststaendigkeit" />} />
            <Route path="/ratgeber/a1-bescheinigung" element={<GuidePage slug="a1-bescheinigung" />} />
            <Route path="/ratgeber/gobd-zeiterfassung" element={<GuidePage slug="gobd-zeiterfassung" />} />
            <Route path="/ratgeber/fks-pruefung" element={<GuidePage slug="fks-pruefung" />} />
            <Route path="/ratgeber/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/invite/worker/:token" element={<Activate />} />
            <Route path="/invite/team/:token" element={<Activate />} />
            <Route path="/t/:token" element={<ClockScreen />} />
            <Route path="/care/scan/:token" element={<CareScan />} />
            <Route path="/pin/:token" element={<PinReveal />} />
            <Route path="/terminal/:token" element={<ClockTerminal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/app/employer" element={<ProtectedRoute allow={EMP}><EmployerLayout /></ProtectedRoute>}>
              <Route index element={<EmployerOverview />} />
              <Route path="employees" element={<Employees />} />
              <Route path="employees/new" element={<EmployeeWizard />} />
              <Route path="employees/:id" element={<EmployeeDetail />} />
              <Route path="employees/:id/edit" element={<EmployeeForm />} />
              <Route path="immigration" element={<ImmigrationCases />} />
              <Route path="immigration/:id" element={<ImmigrationCaseDetail />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="contracts/:id" element={<ContractDetail />} />
              <Route path="documents" element={<EmployerDocuments />} />
              <Route path="deadlines" element={<EmployerDeadlines />} />
              <Route path="time" element={<TimeDashboard />} />
              <Route path="time/upgrade" element={<TimeUpgrade />} />
              <Route path="time/financials" element={<TimeFinancials />} />
              <Route path="time/compliance" element={<ComplianceCenter />} />
              <Route path="time/corrections" element={<CorrectionRequests />} />
              <Route path="profile-confirmations" element={<ProfileChangeRequests />} />
              <Route path="document-verifications" element={<DocumentVerifications />} />
              <Route path="modules" element={<EmployerModules />} />
              <Route element={<CareGate />}>
              <Route path="pflege" element={<CarePflege />} />
              <Route path="pflege/patients" element={<CarePatients />} />
              <Route path="pflege/patients/new" element={<CarePatientNew />} />
              <Route path="pflege/patients/:id" element={<CarePatientDetail />} />
              <Route path="pflege/dispatch" element={<CareDispatch />} />
              <Route path="pflege/tours" element={<CareTours />} />
              <Route path="pflege/tours/:id" element={<CareTourDetail />} />
              <Route path="pflege/visits" element={<CareVisits />} />
              <Route path="pflege/visits/new" element={<CareVisitNew />} />
              <Route path="pflege/visits/:id" element={<CareVisitDetail />} />
              <Route path="pflege/tags" element={<CareTags />} />
              <Route path="pflege/reports" element={<CareReports />} />
              </Route>
              <Route path="time/reports" element={<TimeReports />} />
              <Route path="time/hardware" element={<TimeHardware />} />
              <Route path="time/tag-analytics" element={<TimeTagAnalytics />} />
              <Route path="time/nfc-tags" element={<NfcDashboard />} />
              <Route path="time/access" element={<SiteAccess />} />
              <Route path="renewals" element={<Renewals />} />
              <Route path="baustellen" element={<Baustellen />} />
              <Route path="baustellen/:id" element={<BaustelleDetail />} />
              <Route path="compliance-news" element={<EmployerComplianceNews />} />
              <Route path="activity" element={<EmployerActivity />} />
              <Route path="company" element={<Company />} />
              <Route path="team" element={<Team />} />
              <Route path="billing" element={<Billing />} />
              <Route path="subcontractors" element={<Subcontractors />}>
                <Route index element={<SubOverview />} />
                <Route path="companies" element={<SubCompanies />} />
                <Route path="companies/:id" element={<SubCompanyDetail />} />
                <Route path="workers" element={<SubWorkers />} />
                <Route path="sites" element={<Navigate to="/app/employer/baustellen" replace />} />
                <Route path="documents" element={<SubDocuments />} />
                <Route path="contracts" element={<SubContracts />} />
                <Route path="contracts/:id" element={<SubContractWizard />} />
                <Route path="compliance" element={<SubCompliance />} />
                <Route path="alerts" element={<SubAlerts />} />
                <Route path="reports" element={<SubReports />} />
              </Route>
              <Route path="settings" element={<EmployerSettings />} />
              <Route path="support" element={<SupportPage />} />
            </Route>

            <Route path="/app/worker" element={<ProtectedRoute allow={["EMPLOYEE"]}><WorkerLayout /></ProtectedRoute>}>
              <Route index element={<WorkerOverview />} />
              <Route path="documents" element={<WorkerDocuments />} />
              <Route path="deadlines" element={<WorkerDeadlines />} />
              <Route path="profile" element={<WorkerProfile />} />
              <Route path="settings" element={<WorkerSettings />} />
              <Route path="time" element={<WorkerMyTime />} />
              <Route path="care" element={<WorkerCareTour />} />
              <Route path="care/visits/:id" element={<WorkerCareVisitDetail />} />
              <Route path="care/day" element={<WorkerCareDay />} />
              <Route path="support" element={<SupportPage />} />
            </Route>

            <Route path="/admin" element={<ProtectedRoute allow={ADM}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="companies" element={<AdminCompanies />} />
              <Route path="companies/:id" element={<AdminCompanyDetail />} />
              <Route path="service-orders" element={<ServiceOrders />} />
              <Route path="immigration-rules" element={<ImmigrationRules />} />
              <Route path="subcon-rules" element={<AdminSubconRules />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="audit-logs" element={<AdminAudit />} />
              <Route path="support" element={<AdminSupport />} />
              <Route element={<AdminFullGuard />}>
                <Route path="employees" element={<AdminEmployees />} />
                <Route path="reminders" element={<AdminReminders />} />
                <Route path="subscriptions" element={<AdminSubscriptions />} />
                <Route path="traffic" element={<AdminTraffic />} />
                <Route path="reference-data/tax-offices" element={<AdminTaxOffices />} />                <Route path="reference-data/time-compliance" element={<AdminReferenceData />} />
                <Route path="compliance-news" element={<AdminComplianceNews />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="hardware" element={<AdminHardware />} />
                <Route path="hardware/nfc-tags" element={<AdminNfcTags />} />
              <Route path="care/nfc-tags" element={<AdminCareTags />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
